import { ValidationError, validateEnvelope } from "../core/schema";
import { verifyEnvelopeSignature } from "../core/signing";
import { deriveStampId } from "../core/stamp_id";
import type { Envelope, VerifyApiResponse } from "../types";
import { type EsploraClient, EsploraError } from "./esplora";
import type { VerificationResult } from "./result";

export interface PipelineDeps {
  esplora?: EsploraClient;
  minConfirmations?: number;
}

export async function runPipeline(
  input: VerifyApiResponse,
  deps: PipelineDeps = {},
): Promise<VerificationResult> {
  const result: VerificationResult = {
    stampId: input.envelope.stamp_id,
    envelope: input.envelope,
    signature: input.signature,
    publicKeyB64: input.sigil_public_key_b64,
    otsState: input.ots_state,
    bitcoinAnchor: null,
    steps: {
      schema: { status: "skipped" },
      stampId: { status: "skipped" },
      signature: { status: "skipped" },
      bitcoin: { status: "skipped" },
    },
    signatureVerified: false,
  };

  // 1. Schema validate.
  try {
    validateEnvelope(input.envelope as Envelope);
    result.steps.schema = { status: "ok" };
  } catch (e) {
    result.steps.schema = {
      status: "failed",
      message: e instanceof ValidationError ? e.message : String(e),
    };
    return result;
  }

  // 2. stamp_id self-derivation.
  const derived = deriveStampId(input.envelope as unknown as Record<string, unknown>);
  if (derived !== input.envelope.stamp_id) {
    result.steps.stampId = {
      status: "failed",
      message: `derived ${derived} != envelope.stamp_id ${input.envelope.stamp_id}`,
    };
    return result;
  }
  result.steps.stampId = { status: "ok" };

  // 3. Signature verify.
  const sigOk = await verifyEnvelopeSignature(
    input.envelope as unknown as Record<string, unknown>,
    input.signature,
    input.sigil_public_key_b64,
  );
  if (!sigOk) {
    result.steps.signature = { status: "failed", message: "signature does not verify" };
    return result;
  }
  result.steps.signature = { status: "ok" };
  result.signatureVerified = true;

  // 4. Bitcoin anchor (informational).
  if (input.ots_state === "confirmed" && input.ots_block_height !== null && deps.esplora) {
    try {
      const confirmations = await deps.esplora.confirmationsForBlock(input.ots_block_height);
      const blockTimestamp = await deps.esplora.blockTimestamp(input.ots_block_height);
      result.bitcoinAnchor = {
        blockHeight: input.ots_block_height,
        confirmations,
        blockTimestamp,
      };
      const minConf = deps.minConfirmations ?? 6;
      if (confirmations >= minConf) {
        result.steps.bitcoin = { status: "ok" };
      } else {
        result.steps.bitcoin = {
          status: "failed",
          message: `block depth ${confirmations} < ${minConf}`,
        };
      }
    } catch (e) {
      result.steps.bitcoin = {
        status: "failed",
        message: e instanceof EsploraError ? e.message : String(e),
      };
    }
  } else if (input.ots_state === "submitted") {
    result.steps.bitcoin = {
      status: "skipped",
      message:
        "awaiting Bitcoin anchoring via OpenTimestamps. This typically takes " +
        "hours, but can take days when Bitcoin fees are high or OTS calendars " +
        "are congested. The signature and timestamp above are already " +
        "cryptographically verified; Bitcoin anchoring is supplementary.",
    };
  } else if (input.ots_state === "pending") {
    result.steps.bitcoin = {
      status: "skipped",
      message: "OTS submission not yet sent to calendars",
    };
  }

  return result;
}
